import type { TranslationMap } from "../lib/types.ts";

export const zh_CN: TranslationMap = {
  common: {
    health: "健康状况",
    ok: "正常",
    online: "在线",
    offline: "离线",
    connect: "连接",
    refresh: "刷新",
    enabled: "已启用",
    disabled: "已禁用",
    na: "不适用",
    version: "版本",
    docs: "文档",
    resources: "资源",
    search: "搜索",
  },
  nav: {
    chat: "聊天",
    control: "控制",
    agent: "代理",
    settings: "设置",
    expand: "展开侧边栏",
    collapse: "折叠侧边栏",
    resize: "调整侧边栏大小",
  },
  tabs: {
    agents: "代理",
    overview: "概览",
    channels: "频道",
    instances: "实例",
    sessions: "会话",
    cron: "定时任务",
    nodes: "节点",
    chat: "聊天",
    config: "配置",
    communications: "通信",
    appearance: "外观与设置",
    automation: "自动化",
    infrastructure: "基础设施",
    aiAgents: "AI 与代理",
    debug: "调试",
    logs: "日志",
  },
  subtitles: {
    agents: "工作区、工具、身份。",
    overview: "状态、入口点、健康。",
    channels: "频道和设置。",
    instances: "已连接客户端和节点。",
    sessions: "活动会话和默认设置。",
    cron: "唤醒和重复运行。",
    nodes: "配对设备和命令。",
    chat: "网关聊天，快速干预。",
    config: "编辑 remoteclaw.json。",
    communications: "频道、消息和音频设置。",
    appearance: "主题、界面和设置向导设置。",
    automation: "命令、钩子、定时任务和插件设置。",
    infrastructure: "网关、Web、浏览器和媒体设置。",
    aiAgents: "代理、模型、技能、工具、记忆和会话设置。",
    debug: "快照、事件、RPC。",
    logs: "实时网关日志。",
  },
  overview: {
    access: {
      title: "网关访问",
      subtitle: "仪表板连接的位置及其身份验证方式。",
      wsUrl: "WebSocket URL",
      token: "网关令牌",
      password: "密码 (不存储)",
      sessionKey: "默认会话密钥",
      language: "语言",
      connectHint: "点击连接以应用连接更改。",
      trustedProxy: "通过受信任代理认证。",
    },
    snapshot: {
      title: "快照",
      subtitle: "最新的网关握手信息。",
      status: "状态",
      uptime: "运行时间",
      tickInterval: "刻度间隔",
      lastChannelsRefresh: "最后频道刷新",
      channelsHint: "使用频道链接 WhatsApp、Telegram、Discord、Signal 或 iMessage。",
    },
    stats: {
      instances: "实例",
      instancesHint: "过去 5 分钟内的在线信号。",
      sessions: "会话",
      sessionsHint: "网关跟踪的最近会话密钥。",
      cron: "定时任务",
      cronNext: "下次唤醒 {time}",
    },
    notes: {
      title: "备注",
      subtitle: "远程控制设置的快速提醒。",
      tailscaleTitle: "Tailscale serve",
      tailscaleText: "首选 serve 模式以通过 tailnet 身份验证将网关保持在回环地址。",
      sessionTitle: "会话清理",
      sessionText: "使用 /new 或 sessions.patch 重置上下文。",
      cronTitle: "定时任务提醒",
      cronText: "为重复运行使用隔离的会话。",
    },
    auth: {
      required: "此网关需要身份验证。添加令牌或密码，然后点击连接。",
      failed: "身份验证失败。请使用 {command} 重新复制令牌化 URL，或更新令牌，然后点击连接。",
    },
    pairing: {
      hint: "此设备需要网关主机的配对批准。",
      mobileHint:
        "在手机上？从桌面运行 remoteclaw dashboard --no-open 复制完整 URL（包括 #token=...）。",
    },
    insecure: {
      hint: "此页面为 HTTP，因此浏览器阻止设备标识。请使用 HTTPS (Tailscale Serve) 或在网关主机上打开 {url}。",
      stayHttp: "如果您必须保持 HTTP，请设置 {config} (仅限令牌)。",
    },
    connection: {
      title: "如何连接",
      step1: "在主机上启动网关：",
      step2: "获取带令牌的仪表盘 URL：",
      step3: "将 WebSocket URL 和令牌粘贴到上方，或直接打开带令牌的 URL。",
      step4: "或生成可重复使用的令牌：",
      docsHint: "如需远程访问，建议使用 Tailscale Serve。",
      docsLink: "查看文档 →",
    },
    cards: {
      cost: "费用",
      recentSessions: "最近会话",
    },
    attention: {
      title: "注意事项",
    },
    eventLog: {
      title: "事件日志",
    },
    logTail: {
      title: "网关日志",
    },
    quickActions: {
      newSession: "新建会话",
      automation: "自动化",
      refreshAll: "全部刷新",
      terminal: "终端",
    },
    palette: {
      placeholder: "输入命令…",
      noResults: "无结果",
    },
  },
  login: {
    subtitle: "网关仪表盘",
    passwordPlaceholder: "可选",
  },
  chat: {
    disconnected: "已断开与网关的连接。",
    refreshTitle: "刷新聊天数据",
    thinkingToggle: "切换助手思考/工作输出",
    focusToggle: "切换专注模式 (隐藏侧边栏 + 页面页眉)",
    hideCronSessions: "隐藏定时任务会话",
    showCronSessions: "显示定时任务会话",
    showCronSessionsHidden: "显示定时任务会话 (已隐藏 {count} 个)",
    onboardingDisabled: "引导期间禁用",
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (简体中文)",
    zhTW: "繁體中文 (繁体中文)",
    ptBR: "Português (巴西葡萄牙语)",
    de: "Deutsch (德语)",
    es: "Español (西班牙语)",
  },
  cron: {
    summary: {
      enabled: "已启用",
      yes: "是",
      no: "否",
      jobs: "任务数",
      nextWake: "下次唤醒",
      refreshing: "刷新中...",
      refresh: "刷新",
    },
    jobs: {
      title: "任务列表",
      subtitle: "网关中存储的所有定时任务。",
      shownOf: "显示 {shown} / 共 {total}",
      searchJobs: "搜索任务",
      searchPlaceholder: "名称、描述或代理",
      enabled: "启用状态",
      all: "全部",
      sort: "排序",
      nextRun: "下次运行",
      recentlyUpdated: "最近更新",
      name: "名称",
      direction: "方向",
      ascending: "升序",
      descending: "降序",
      noMatching: "没有匹配的任务。",
      loading: "加载中...",
      loadMore: "加载更多任务",
    },
    runs: {
      title: "运行历史",
      subtitleAll: "所有任务的最新运行记录。",
      subtitleJob: "{title} 的最新运行记录。",
      scope: "范围",
      allJobs: "所有任务",
      selectedJob: "已选任务",
      searchRuns: "搜索运行",
      searchPlaceholder: "摘要、错误或任务",
      newestFirst: "最新优先",
      oldestFirst: "最早优先",
      status: "状态",
      delivery: "投递",
      clear: "清除",
      allStatuses: "全部状态",
      allDelivery: "全部投递",
      selectJobHint: "请选择一个任务以查看运行历史。",
      noMatching: "没有匹配的运行记录。",
      loadMore: "加载更多运行",
      runStatusOk: "成功",
      runStatusError: "错误",
      runStatusSkipped: "已跳过",
      runStatusUnknown: "未知",
      deliveryDelivered: "已投递",
      deliveryNotDelivered: "未投递",
      deliveryUnknown: "未知",
      deliveryNotRequested: "未请求",
    },
    form: {
      editJob: "编辑任务",
      newJob: "新建任务",
      updateSubtitle: "更新所选定时任务。",
      createSubtitle: "创建定时唤醒或代理运行。",
      required: "必填",
      requiredSr: "必填",
      basics: "基本信息",
      basicsSub: "命名、选择助手并设置启用状态。",
      fieldName: "名称",
      description: "描述",
      agentId: "代理 ID",
      namePlaceholder: "晨间简报",
      descriptionPlaceholder: "此任务的可选说明",
      agentPlaceholder: "main 或 ops",
      agentHelp: "输入以选择已知代理，或输入自定义 ID。",
      schedule: "调度",
      scheduleSub: "控制任务运行时间。",
      every: "每隔",
      at: "指定时间",
      cronOption: "Cron",
      runAt: "运行时间",
      unit: "单位",
      minutes: "分钟",
      hours: "小时",
      days: "天",
      expression: "表达式",
      expressionPlaceholder: "0 7 * * *",
      everyAmountPlaceholder: "30",
      timezoneOptional: "时区（可选）",
      timezonePlaceholder: "America/Los_Angeles",
      timezoneHelp: "选择常用时区或输入有效的 IANA 时区。",
      jitterHelp: "需要抖动？使用高级 → 抖动窗口 / 抖动单位。",
      execution: "执行",
      executionSub: "选择唤醒时机和任务执行内容。",
      session: "会话",
      main: "主会话",
      isolated: "隔离会话",
      sessionHelp: "主会话发布系统事件。隔离会话运行独立的代理轮次。",
      wakeMode: "唤醒模式",
      now: "立即",
      nextHeartbeat: "下次心跳",
      wakeModeHelp: "立即模式立即触发。下次心跳等待下一个周期。",
      payloadKind: "执行内容",
      systemEvent: "发布消息到主时间线",
      agentTurn: "运行助手任务（隔离）",
      systemEventHelp: "将文本发送到网关主时间线（适用于提醒/触发）。",
      agentTurnHelp: "使用您的提示在独立会话中启动助手运行。",
      timeoutSeconds: "超时（秒）",
      timeoutPlaceholder: "可选，如 90",
      timeoutHelp: "可选。留空以使用网关默认超时行为。",
      mainTimelineMessage: "主时间线消息",
      assistantTaskPrompt: "助手任务提示",
      deliverySection: "投递",
      deliverySub: "选择运行摘要的发送位置。",
      resultDelivery: "结果投递",
      announceDefault: "发布摘要（默认）",
      webhookPost: "Webhook POST",
      noneInternal: "无（仅内部）",
      deliveryHelp: "发布将摘要发送到聊天。无保持执行仅内部。",
      webhookUrl: "Webhook URL",
      channel: "频道",
      webhookPlaceholder: "https://example.com/cron",
      channelHelp: "选择接收摘要的已连接频道。",
      webhookHelp: "将运行摘要发送到 Webhook 端点。",
      to: "收件人",
      toPlaceholder: "+1555... 或聊天 ID",
      toHelp: "可选收件人覆盖（聊天 ID、电话或用户 ID）。",
      advanced: "高级",
      advancedHelp: "投递保证、调度抖动和模型控制的可选覆盖。",
      deleteAfterRun: "运行后删除",
      deleteAfterRunHelp: "适用于应自动清理的一次性提醒。",
      clearAgentOverride: "清除代理覆盖",
      clearAgentHelp: "强制此任务使用网关默认助手。",
      exactTiming: "精确时间（无抖动）",
      exactTimingHelp: "在精确的 cron 边界运行，无分散。",
      staggerWindow: "抖动窗口",
      staggerUnit: "抖动单位",
      staggerPlaceholder: "30",
      seconds: "秒",
      model: "模型",
      modelPlaceholder: "openai/gpt-5.2",
      modelHelp: "输入以选择已知模型，或输入自定义模型。",
      thinking: "思考",
      thinkingPlaceholder: "low",
      thinkingHelp: "使用建议级别或输入提供商特定值。",
      bestEffortDelivery: "尽力投递",
      bestEffortHelp: "投递失败时不使任务失败。",
      cantAddYet: "暂无法添加任务",
      fillRequired: "填写下方必填项以启用提交。",
      fixFields: "修复 {count} 个字段以继续。",
      fixFieldsPlural: "修复 {count} 个字段以继续。",
      saving: "保存中...",
      saveChanges: "保存更改",
      addJob: "添加任务",
      cancel: "取消",
    },
    jobList: {
      allJobs: "所有任务",
      selectJob: "（选择任务）",
      enabled: "已启用",
      disabled: "已禁用",
      edit: "编辑",
      clone: "克隆",
      disable: "禁用",
      enable: "启用",
      run: "运行",
      history: "历史",
      remove: "删除",
    },
    jobDetail: {
      system: "系统",
      prompt: "提示",
      delivery: "投递",
      agent: "代理",
    },
    jobState: {
      status: "状态",
      next: "下次",
      last: "上次",
    },
    runEntry: {
      noSummary: "无摘要。",
      runAt: "运行于",
      openRunChat: "打开运行聊天",
      next: "下次 {rel}",
      due: "到期 {rel}",
    },
    errors: {
      nameRequired: "名称为必填项。",
      scheduleAtInvalid: "请输入有效的日期/时间。",
      everyAmountInvalid: "间隔必须大于 0。",
      cronExprRequired: "Cron 表达式为必填项。",
      staggerAmountInvalid: "抖动值必须大于 0。",
      systemTextRequired: "系统文本为必填项。",
      agentMessageRequired: "代理消息为必填项。",
      timeoutInvalid: "若设置超时，必须大于 0 秒。",
      webhookUrlRequired: "Webhook URL 为必填项。",
      webhookUrlInvalid: "Webhook URL 必须以 http:// 或 https:// 开头。",
      invalidRunTime: "无效的运行时间。",
      invalidIntervalAmount: "无效的间隔值。",
      cronExprRequiredShort: "Cron 表达式为必填。",
      invalidStaggerAmount: "无效的抖动值。",
      systemEventTextRequired: "系统事件文本为必填。",
      agentMessageRequiredShort: "代理消息为必填。",
      nameRequiredShort: "名称为必填。",
    },
  },
};
